import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import Footer from '../pages/_page_components/Footer';
import SignIn from '../pages/sign-in/SignIn.jsx';
import AppTheme from '../shared-theme/AppTheme';
import { CacSimulationProvider } from '../signing/simulation/CacSimulationProvider';
import MenuTop from './components/headerMenu/MenuTop';
import PersistentLeftDrawer from './components/PersistentLeftDrawer.jsx';
import NoRolesWelcome from './components/NoRolesWelcome.jsx';
import { routes } from './consts.jsx';
import { clearLocalStoragePreservingPreferences } from '../app/user/logoutUtils';
import {
  ContentContainer,
  DashboardContainer,
  RouteContainer,
} from './StyledComponents.jsx';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

import { useCookies } from 'react-cookie';
import {
  useLazyGetApiFormsTypesQuery,
  useLazyGetApiTokenQuery,
  useLazyGetApiUsersMeQuery,
} from '../app/athenaApiGen.js';
import {
  setAvailableForms,
  setLoginToken,
  setUserProfile,
} from '../app/user/userStateSlice.js';
import { getEnabledMenuItems, hasAnyAdminPermission } from '../pages/utilities.js';

const Dashboard = () => {
  const [cookies, removeCookie] = useCookies(['login_token']);
  const { profile, actions, loggedIn, token_exp, availableForms, actionsLoaded } = useSelector(
    (state) => state.user,
  );
  const [getToken] = useLazyGetApiTokenQuery();
  const [getProfile] = useLazyGetApiUsersMeQuery();
  const [getApiFormTypes] = useLazyGetApiFormsTypesQuery();
  const dispatch = useDispatch();

  // Notification state for session expiration
  const [notificationOpen, setNotificationOpen] = React.useState(false);
  const [notificationMessage, setNotificationMessage] = React.useState('');
  const enabledItems = getEnabledMenuItems(actions, availableForms);
  const hasNoRoles = loggedIn && actionsLoaded && actions.length === 0;

  const handleNotificationClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setNotificationOpen(false);
  };

  React.useEffect(() => {
    if (!loggedIn) {
      // let's check if we're logging in now
      if (!profile && cookies.login_token) {
        getToken({
          loginToken: cookies.login_token,
        })
          .unwrap()
          .then(async (data) => {
            dispatch(setLoginToken(data));
            const profile = await getProfile().unwrap();
            dispatch(setUserProfile(profile));
            // Actions are extracted from profile.roles in setUserProfile reducer
            // actionsLoaded is also set to true there

            // Fetch available form types to gate form-based navigation
            const formTypes = await getApiFormTypes().unwrap();
            dispatch(setAvailableForms(formTypes.filter((f) => !f.deprecated)));
          })
          .catch((err) => {
            console.error('Failed to fetch user data:', err);
          });
      }
    }
  }, [cookies.login_token, dispatch, getToken, getProfile, getApiFormTypes, loggedIn, profile]);

  // Token refresh logic - refresh 5 minutes before expiration
  React.useEffect(() => {
    if (!loggedIn || !profile || !token_exp) return;

    const expiryTime = token_exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiry = expiryTime - currentTime;
    const refreshTime = timeUntilExpiry - 5 * 60 * 1000; // 5 minutes before expiration

    // Only schedule refresh if there's time remaining
    if (refreshTime > 0) {
      const timerId = setTimeout(() => {
        setNotificationMessage('Attempting to refresh session...');
        setNotificationOpen(true);
        getToken({
          loginToken: cookies.login_token,
        })
          .unwrap()
          .then(async (data) => {
            setNotificationOpen(false);
            dispatch(setLoginToken(data));
            const profile = await getProfile().unwrap();
            dispatch(setUserProfile(profile));

            // Re-fetch available form types on token refresh
            const formTypes = await getApiFormTypes().unwrap();
            dispatch(setAvailableForms(formTypes.filter((f) => !f.deprecated)));
          })
          .catch((err) => {
            console.error('Token refresh failed:', err);
            // Show notification that session has expired
            setNotificationMessage('Your session has expired. Logging out...');
            setNotificationOpen(true);

            // Wait a moment for notification to show, then logout
            setTimeout(() => {
              removeCookie('login_token', null);
              setNotificationOpen(false);
              clearLocalStoragePreservingPreferences();
              sessionStorage.clear();
              window.location.href = '/sign-in';
            }, 10000);
          });
      }, refreshTime);

      return () => clearTimeout(timerId);
    } else {
      // Token already expired or will expire soon
      console.warn('Token is expired or will expire very soon');
    }
  }, [
    loggedIn,
    profile,
    token_exp,
    cookies.login_token,
    getProfile,
    getToken,
    getApiFormTypes,
    dispatch,
    removeCookie,
  ]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DashboardContainer>
        {!loggedIn && <SignIn />}
        {loggedIn && (
          <AppTheme themeComponents={xThemeComponents}>
            <CacSimulationProvider>
            <MenuTop />
            {hasNoRoles ? (
              <NoRolesWelcome />
            ) : (
            <ContentContainer>
              <PersistentLeftDrawer />
              <RouteContainer>
                <Grid
                  container
                  component="main"
                  spacing={1}
                  flexGrow={1}
                  flexDirection={'column'}
                  sx={{ minHeight: 0 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      height: '100%',
                      overflowX: 'hidden',
                    }}
                  >
                    <Stack sx={{ mx: 3, height: '100%' }}>
                      <Routes>
                        {routes.map(({ path, element }) => {
                          const isRouteAccessible =
                            (path === '/' && enabledItems.home) ||
                            // D.4 admin — one gate for everything under /admin.
                            // AdminLanding handles per-tab / per-section / per-item
                            // gating internally. Settings path (/settings) is a
                            // separate user-facing surface; /admin/settings and
                            // related legacy paths redirect to /admin/platform.
                            (path.startsWith('/admin') &&
                              hasAnyAdminPermission(enabledItems)) ||
                            (path.toLowerCase().includes('settings') &&
                              path !== '/settings' &&
                              enabledItems.adminSidewideAccess) ||
                            // /financials is always registered; the page
                            // itself gates on `view-financial-views` and shows
                            // a role-requirement message when absent (ATH-1570).
                            path === '/financials' ||
                            (path === '/projects' && enabledItems.projects) ||
                            (path === '/master-schedule' && enabledItems.ims) ||
                            (path === '/library' && enabledItems.library) ||
                            (path === '/deliverables' &&
                              enabledItems.deliverables) ||
                            (path === '/deliverables' &&
                              enabledItems.deliverables) ||
                            // The `/:id` form routes are also opened as
                            // read-only from the approval queue's "View form"
                            // link, so approvers (Site Lead, APM, COR) need
                            // route access even when their role can't create
                            // the form type from the Forms menu. The list and
                            // create paths stay gated by form-create scope.
                            ((path === '/ctp-list' || path === '/ctp') &&
                              enabledItems.forms.ctp) ||
                            (path === '/ctp/:id' &&
                              (enabledItems.forms.ctp ||
                                enabledItems.approvalQueue)) ||
                            ((path === '/tr-list' || path === '/tr') &&
                              enabledItems.forms.tr) ||
                            (path === '/tr/:id' &&
                              (enabledItems.forms.tr ||
                                enabledItems.approvalQueue)) ||
                            ((path === '/rip-list' || path === '/rip') &&
                              enabledItems.forms.rip) ||
                            (path === '/rip/:id' &&
                              (enabledItems.forms.rip ||
                                enabledItems.approvalQueue)) ||
                            ((path === '/tar-list' || path === '/tar') &&
                              enabledItems.forms.tar) ||
                            (path === '/tar/:id' &&
                              (enabledItems.forms.tar ||
                                enabledItems.approvalQueue)) ||
                            ((path === '/tar-imcs-list' ||
                              path === '/tar-imcs') &&
                              enabledItems.forms.tar) ||
                            (path === '/tar-imcs/:id' &&
                              (enabledItems.forms.tar ||
                                enabledItems.approvalQueue)) ||
                            ((path === '/pnr-list' || path === '/pnr') &&
                              enabledItems.forms.pnr) ||
                            (path === '/pnr/:id' &&
                              (enabledItems.forms.pnr ||
                                enabledItems.approvalQueue)) ||
                            (path === '/approvalQueue' &&
                              enabledItems.approvalQueue) ||
                            path === '/notifications' ||
                            path === '/about' ||
                            path === '/help' ||
                            path === '/preferences';

                          return isRouteAccessible ? (
                            <Route key={path} path={path} element={element} />
                          ) : null;
                        })}
                      </Routes>
                    </Stack>
                  </Box>
                </Grid>
              </RouteContainer>
            </ContentContainer>
            )}
            {/* Footer */}
            <Box
              component="footer"
              sx={{
                width: '100%',
                flexShrink: 0,
                bgcolor: '#154F85',
              }}
            >
              <Footer />
            </Box>
            </CacSimulationProvider>
          </AppTheme>
        )}
      </DashboardContainer>
      <Snackbar
        open={notificationOpen}
        autoHideDuration={4000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notificationMessage}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default Dashboard;
